import {
  Button,
  Input,
  InputSelect,
  InputWrapper,
  type SelectItem,
} from '@vrplatform/components';
import {
  type RequestBody,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
  useTeam,
} from '@vrplatform/data';
import { Box, Group, Modal } from '@mantine/core';
import type { accountReservationRevRec_enum } from '@vrplatform/graphql';
import { showSuccessNotification } from '@vrplatform/theme';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useRevenueRecognitionOptions } from './useRevenueRecognitionOptions';
import { formatManualReservationLineType } from './ReservationLineTypeAddModal.utils';
import { useReservationLineTypeAccountOptionsQuery } from './useReservationLineTypeAccountOptionsQuery';

export type ReservationLineTypeAddModalSubmitInput =
  RequestBody<'post:/reservations/line-mappings'>;

export type ReservationLineTypeAddModalCreatedOutput = {
  type: string;
};

type ReservationLineTypeAddModalProps = {
  opened: boolean;
  closeModal: () => void;
  onCreated?: (created: ReservationLineTypeAddModalCreatedOutput) => void;
};

type FormValues = {
  lineName: string;
  accountId: string | null;
  revenueRecognition: accountReservationRevRec_enum | null;
};

const trapSpacesForRequiredFields = (value: string) =>
  !!value.trim() || 'Line name is required';

const initialValues: FormValues = {
  lineName: '',
  accountId: null,
  revenueRecognition: null,
};

export const ReservationLineTypeAddModal = ({
  opened,
  closeModal: cl,
  onCreated,
}: ReservationLineTypeAddModalProps) => {
  const [team] = useTeam();

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
  });

  const invalidate = useInvalidateApiQueries();

  const { mutateAsync: createReservationLineType } = useApiMutation(
    'post',
    '/reservations/line-mappings'
  );

  const {
    data: accountOptions = [],
    isLoading: accountLoading,
    error: accountError,
  } = useReservationLineTypeAccountOptionsQuery({
    skip: !opened,
  });

  const options = useRevenueRecognitionOptions();

  const defaultRevenueRecognition = team?.defaultRevenueRecognition;

  const revenueRecognitionOptions = useMemo<SelectItem[]>(() => {
    return options.map((option) => {
      const label =
        option.value === defaultRevenueRecognition
          ? `${option.label} (Default)`
          : option.label;

      return {
        ...option,
        label,
      };
    });
  }, [options, defaultRevenueRecognition]);

  const closeModal = useCallback(() => {
    methods.reset(initialValues);
    cl();
  }, [methods, cl]);

  const submit = methods.handleSubmit(async (values) => {
    const type = formatManualReservationLineType(values.lineName);

    if (!type) {
      methods.setError('lineName', {
        message: 'Line name must include at least one letter',
      });
      return;
    }

    await createReservationLineType({
      body: {
        type,
        description: values.lineName.trim(),
        accountId: values.accountId ?? null,
        revenueRecognition: values.revenueRecognition ?? null,
      },
    })
      .then(() => {
        showSuccessNotification({
          title: 'Line type added',
          message: 'Line type was added successfully.',
        });

        invalidate(['reservations', 'settings']);

        onCreated?.({
          type,
        });

        closeModal();
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'Failed to add line type',
          defaultMessage:
            'Please try again and contact support if the issue persists.',
        });
      });
  });

  const defaultRevRec = options.find(
    (o) => o.value === defaultRevenueRecognition
  )?.label;
  const placeholder = defaultRevRec
    ? `Default: ${defaultRevRec}`
    : 'Select revenue recognition';

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title="Add line type"
      styles={{
        title: {
          fontWeight: 600,
        },
      }}
      centered
      size={480}
      withinPortal
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
        })}
      >
        <Controller
          control={methods.control}
          name="lineName"
          rules={{
            required: 'Line name is required',
            validate: trapSpacesForRequiredFields,
          }}
          render={({ field, fieldState }) => (
            <InputWrapper
              label="Line name"
              error={fieldState.error?.message}
              required
            >
              <Input
                value={field.value}
                setValue={field.onChange}
                error={!!fieldState.error}
                placeholder="E.g. Pet fee"
              />
            </InputWrapper>
          )}
        />

        <Controller
          control={methods.control}
          name="accountId"
          rules={{
            required: 'Account is required',
          }}
          render={({ field, fieldState }) => {
            const value =
              accountOptions.find((option) => option.value === field.value) ??
              null;

            return (
              <InputWrapper
                label="Account"
                required
                error={fieldState.error?.message}
              >
                <InputSelect
                  type="single"
                  value={value}
                  setValue={(value) => field.onChange(value?.value ?? null)}
                  data={{
                    options: accountOptions,
                    loading: accountLoading,
                    error: accountError,
                    sort: null,
                  }}
                  inputProps={{
                    placeholder: 'Select account',
                    withClearButton: true,
                    loadingQuery: accountLoading,
                    error: !!fieldState.error,
                  }}
                  dropdownProps={{
                    withinPortal: true,
                  }}
                />
              </InputWrapper>
            );
          }}
        />

        <Controller
          control={methods.control}
          name="revenueRecognition"
          render={({ field }) => {
            const value =
              revenueRecognitionOptions.find(
                (option) => option.value === field.value
              ) ?? null;

            return (
              <InputWrapper label="Revenue Recognition">
                <InputSelect
                  type="single"
                  value={value}
                  setValue={(value) => field.onChange(value?.value ?? null)}
                  data={{
                    options: revenueRecognitionOptions,
                    sort: null,
                  }}
                  inputProps={{
                    withClearButton: true,
                    placeholder,
                  }}
                  dropdownProps={{
                    withinPortal: true,
                  }}
                />
              </InputWrapper>
            );
          }}
        />

        <Group justify="right" mt="md">
          <Button
            onClick={closeModal}
            disabled={methods.formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={methods.formState.isSubmitting}
          >
            Save line type
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
